<template>
  <AdminLayout>
    <template #breadcrumb>
      <breadcrumb-link view="AdminInquiryList" label="Inquiries" :active="true" />
    </template>

    <div class="box" style="position: relative">
      <b-loading :is-full-page="false" v-model="loading"></b-loading>
      <div class="page-header">
        <h1 class="title is-2 has-text-primary">Inquiry Details</h1>
        <div class="subtitle is-6 has-text-grey"></div>
      </div>
      <div class="details-container">
        <!-- Basic Information Section -->
        <b-collapse class="card" :open="false" aria-id="basicInfo">
          <template #trigger="props">
            <div class="card-header" role="button" aria-controls="basicInfo">
              <p class="card-header-title">
                <b-icon icon="information" class="mr-2"></b-icon>
                Basic Information
              </p>
              <a class="card-header-icon">
                <b-icon :icon="props.open ? 'menu-down' : 'menu-up'"></b-icon>
              </a>
            </div>
          </template>
          <div class="card-content">
            <div class="columns is-multiline">
              <div class="column is-6">
                <p class="detail-item">
                  <span class="detail-label">ID</span>
                  <span class="detail-value">{{ inquiry?.id }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Remote ID</span>
                  <span class="detail-value">{{ inquiry?.remote_id }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Status</span>
                  <span class="detail-value">
                    <span class="tag" :class="getStatusClass(inquiry?.status)">
                      {{ inquiry?.status }}
                    </span>
                  </span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Plan Status</span>
                  <span class="detail-value">
                    <span class="tag" :class="getStatusClass(inquiry?.plan_status)">
                      {{ inquiry?.plan_status }}
                    </span>
                  </span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Source</span>
                  <span class="detail-value">{{ inquiry?.source }}</span>
                </p>
              </div>
              <div class="column is-6">
                <p class="detail-item">
                  <span class="detail-label">Created At</span>
                  <span class="detail-value">{{ formatDate(inquiry?.created_at) }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Updated At</span>
                  <span class="detail-value">{{ formatDate(inquiry?.updated_at) }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">As of Date</span>
                  <span class="detail-value">{{ formatDate(inquiry?.as_of_date) }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Payer Name</span>
                  <span class="detail-value">{{ inquiry?.payer_name_override }}</span>
                </p>
              </div>
            </div>
          </div>
        </b-collapse>

        <!-- Patient Information Section -->
        <b-collapse class="card" :open="false" aria-id="patientInfo">
          <template #trigger="props">
            <div class="card-header" role="button" aria-controls="patientInfo">
              <p class="card-header-title">
                <b-icon icon="account" class="mr-2"></b-icon>
                Patient Information
              </p>
              <a class="card-header-icon">
                <b-icon :icon="props.open ? 'menu-down' : 'menu-up'"></b-icon>
              </a>
            </div>
          </template>
          <div class="card-content">
            <div class="columns is-multiline">
              <div class="column is-6">
                <p class="detail-item">
                  <span class="detail-label">First Name</span>
                  <span class="detail-value">{{ inquiry?.patient_first_name }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Last Name</span>
                  <span class="detail-value">{{ inquiry?.patient_last_name }}</span>
                </p>
              </div>
            </div>
          </div>
        </b-collapse>

        <!-- Organization Information Section -->
        <b-collapse class="card" :open="false" aria-id="orgInfo">
          <template #trigger="props">
            <div class="card-header" role="button" aria-controls="orgInfo">
              <p class="card-header-title">
                <b-icon icon="office-building" class="mr-2"></b-icon>
                Organization Information
              </p>
              <a class="card-header-icon">
                <b-icon :icon="props.open ? 'menu-down' : 'menu-up'"></b-icon>
              </a>
            </div>
          </template>
          <div class="card-content">
            <div class="columns is-multiline">
              <div class="column is-6">
                <p class="detail-item">
                  <span class="detail-label">ID</span>
                  <span class="detail-value">{{ inquiry?.organization?.id }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Name</span>
                  <span class="detail-value">{{ inquiry?.organization?.name }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Status</span>
                  <span class="detail-value">
                    <span
                      class="tag"
                      :class="inquiry?.organization?.is_active ? 'is-success' : 'is-danger'"
                    >
                      {{ inquiry?.organization?.is_active ? 'Active' : 'Inactive' }}
                    </span>
                  </span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Provider Override</span>
                  <span class="detail-value">
                    <span
                      class="tag"
                      :class="
                        inquiry?.organization?.perm_provider_override ? 'is-info' : 'is-light'
                      "
                    >
                      {{ inquiry?.organization?.perm_provider_override ? 'Yes' : 'No' }}
                    </span>
                  </span>
                </p>
              </div>
              <div class="column is-6">
                <p class="detail-item">
                  <span class="detail-label">Created At</span>
                  <span class="detail-value">
                    {{ formatDate(inquiry?.organization?.created_at) }}
                  </span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Updated At</span>
                  <span class="detail-value">
                    {{ formatDate(inquiry?.organization?.updated_at) }}
                  </span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Active Subscription</span>
                  <span class="detail-value">
                    <span
                      class="tag"
                      :class="
                        inquiry?.organization?.has_active_subscription ? 'is-success' : 'is-light'
                      "
                    >
                      {{ inquiry?.organization?.has_active_subscription ? 'Yes' : 'No' }}
                    </span>
                  </span>
                </p>
              </div>
            </div>
          </div>
        </b-collapse>

        <!-- User Information Section -->
        <b-collapse class="card" :open="false" aria-id="userInfo">
          <template #trigger="props">
            <div class="card-header" role="button" aria-controls="userInfo">
              <p class="card-header-title">
                <b-icon icon="account" class="mr-2"></b-icon>
                User Information
              </p>
              <a class="card-header-icon">
                <b-icon :icon="props.open ? 'menu-down' : 'menu-up'"></b-icon>
              </a>
            </div>
          </template>
          <div class="card-content">
            <div class="columns is-multiline">
              <div class="column is-6">
                <p class="detail-item">
                  <span class="detail-label">ID</span>
                  <span class="detail-value">{{ inquiry?.user?.id }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Name</span>
                  <span class="detail-value">
                    {{ inquiry?.user?.first_name }} {{ inquiry?.user?.last_name }}
                  </span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Email</span>
                  <span class="detail-value">{{ inquiry?.user?.email }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Phone</span>
                  <span class="detail-value">{{ inquiry?.user?.phone }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Role</span>
                  <span class="detail-value">{{ inquiry?.user?.is_admin ? 'Admin' : 'User' }}</span>
                </p>
              </div>
              <div class="column is-6">
                <p class="detail-item">
                  <span class="detail-label">Last Login</span>
                  <span class="detail-value">{{ formatDate(inquiry?.user?.last_login) }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">TOS Accepted</span>
                  <span class="detail-value">{{ formatDate(inquiry?.user?.tos_accepted_at) }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Status</span>
                  <span class="detail-value">
                    <span
                      class="tag"
                      :class="inquiry?.user?.is_active ? 'is-success' : 'is-danger'"
                    >
                      {{ inquiry?.user?.is_active ? 'Active' : 'Inactive' }}
                    </span>
                  </span>
                </p>
              </div>
            </div>
          </div>
        </b-collapse>

        <!-- Payer Information Section -->
        <b-collapse class="card" :open="false" aria-id="payerInfo">
          <template #trigger="props">
            <div class="card-header" role="button" aria-controls="payerInfo">
              <p class="card-header-title">
                <b-icon icon="credit-card" class="mr-2"></b-icon>
                Payer Information
              </p>
              <a class="card-header-icon">
                <b-icon :icon="props.open ? 'menu-down' : 'menu-up'"></b-icon>
              </a>
            </div>
          </template>
          <div class="card-content">
            <div class="columns is-multiline">
              <div class="column is-6">
                <p class="detail-item">
                  <span class="detail-label">ID</span>
                  <span class="detail-value">{{ inquiry?.payer?.id }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Name</span>
                  <span class="detail-value">{{ inquiry?.payer?.name }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Internal Name</span>
                  <span class="detail-value">{{ inquiry?.payer?.name_internal }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Code</span>
                  <span class="detail-value">{{ inquiry?.payer?.code }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Type</span>
                  <span class="detail-value">
                    {{ inquiry?.payer?.is_blue ? 'Blue' : 'Non-Blue' }}
                  </span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Status</span>
                  <span class="detail-value">
                    <span
                      class="tag"
                      :class="inquiry?.payer?.is_active ? 'is-success' : 'is-danger'"
                    >
                      {{ inquiry?.payer?.is_active ? 'Active' : 'Inactive' }}
                    </span>
                  </span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Available</span>
                  <span class="detail-value">
                    {{ inquiry?.payer?.is_available ? 'Yes' : 'No' }}
                  </span>
                </p>
              </div>
              <div class="column is-6">
                <p class="detail-item">
                  <span class="detail-label">P Name</span>
                  <span class="detail-value">{{ inquiry?.payer?.p_name }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">P Code</span>
                  <span class="detail-value">{{ inquiry?.payer?.p_code }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">P Is Active</span>
                  <span class="detail-value">
                    {{ inquiry?.payer?.p_is_active ? 'Yes' : 'No' }}
                  </span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Created At</span>
                  <span class="detail-value">{{ formatDate(inquiry?.payer?.created_at) }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Updated At</span>
                  <span class="detail-value">{{ formatDate(inquiry?.payer?.updated_at) }}</span>
                </p>
              </div>
            </div>
          </div>
        </b-collapse>

        <!-- Provider Information Section -->
        <b-collapse class="card" :open="false" aria-id="providerInfo">
          <template #trigger="props">
            <div class="card-header" role="button" aria-controls="providerInfo">
              <p class="card-header-title">
                <b-icon icon="hospital" class="mr-2"></b-icon>
                Provider Information
              </p>
              <a class="card-header-icon">
                <b-icon :icon="props.open ? 'menu-down' : 'menu-up'"></b-icon>
              </a>
            </div>
          </template>
          <div class="card-content">
            <div class="columns is-multiline">
              <div class="column is-6">
                <p class="detail-item">
                  <span class="detail-label">ID</span>
                  <span class="detail-value">{{ inquiry?.provider?.id }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Name</span>
                  <span class="detail-value">{{ inquiry?.provider?.name }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">NPI</span>
                  <span class="detail-value">{{ inquiry?.provider?.npi }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Tax ID</span>
                  <span class="detail-value">{{ inquiry?.provider?.tax_id }}</span>
                </p>
              </div>
              <div class="column is-6">
                <p class="detail-item">
                  <span class="detail-label">Status</span>
                  <span class="detail-value">
                    <span
                      class="tag"
                      :class="inquiry?.provider?.is_active ? 'is-success' : 'is-danger'"
                    >
                      {{ inquiry?.provider?.is_active ? 'Active' : 'Inactive' }}
                    </span>
                  </span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Created At</span>
                  <span class="detail-value">{{ formatDate(inquiry?.provider?.created_at) }}</span>
                </p>
                <p class="detail-item">
                  <span class="detail-label">Updated At</span>
                  <span class="detail-value">{{ formatDate(inquiry?.provider?.updated_at) }}</span>
                </p>
              </div>
            </div>
          </div>
        </b-collapse>

        <!-- Raw Response Section -->
        <b-collapse class="card" :open="false" aria-id="rawResponse">
          <template #trigger="props">
            <div class="card-header" role="button" aria-controls="rawResponse">
              <p class="card-header-title">
                <b-icon icon="file" class="mr-2"></b-icon>
                Raw Response
              </p>
              <a class="card-header-icon">
                <b-icon :icon="props.open ? 'menu-down' : 'menu-up'"></b-icon>
              </a>
            </div>
          </template>
          <div class="card-content">
            <pre class="raw-response">{{
              inquiry?.coverage?.raw_response
                ? JSON.stringify(inquiry.coverage.raw_response, null, 2)
                : 'No raw response available'
            }}</pre>
          </div>
        </b-collapse>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import AdminLayout from '../../layouts/AdminLayout.vue';
import request from '../../request';

export default {
  name: 'AdminInquiryDetail',
  components: {
    AdminLayout,
  },
  data() {
    return {
      loading: true,
      inquiry: {},
    };
  },
  computed: {
    inquiryId() {
      return this.$route.params.id;
    },
  },
  mounted() {
    this.fetchInquiry();
  },
  methods: {
    fetchInquiry() {
      return request(`/admin/inquiries/${this.inquiryId}`)
        .then((resp) => {
          this.inquiry = resp.data;
          this.loading = false;
        })
        .catch(() => {
          this.$buefy.toast.open({
            message: 'An error occurred while fetching inquiry.',
            type: 'is-danger',
            duration: 5000,
          });
          this.loading = false;
        });
    },
    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleString();
    },
    getStatusClass(status) {
      switch (status?.toLowerCase()) {
        case 'active':
        case 'complete':
          return 'is-success';
        case 'inactive':
        case 'pending':
          return 'is-warning';
        case 'error':
        case 'failed':
          return 'is-danger';
        default:
          return 'is-light';
      }
    },
  },
  watch: {
    inquiryId() {
      this.fetchInquiry();
    },
  },
};
</script>

<style scoped>
.page-header {
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
}

.page-header .title {
  margin-bottom: 0.5rem;
}

.details-container {
  margin-top: 1rem;
}

.card {
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1);
  border-radius: 6px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 4px 6px rgba(10, 10, 10, 0.1);
}

.card-header {
  background-color: #f8f9fa;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #eee;
}

.card-header-title {
  font-weight: 600;
  color: #363636;
  font-size: 1.1rem;
}

.card-content {
  padding: 1.5rem;
}

.detail-item {
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.2s ease;
}

.detail-item:hover {
  background-color: #f0f2f5;
}

.detail-label {
  font-weight: 600;
  color: #4a4a4a;
  min-width: 150px;
}

.detail-value {
  color: #363636;
  text-align: right;
  flex: 1;
  margin-left: 1rem;
}

.raw-response {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 4px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: 'Fira Code', monospace;
  font-size: 0.9rem;
  line-height: 1.5;
  border: 1px solid #eee;
}

.tag {
  font-weight: 500;
  padding: 0.25rem 0.75rem;
}

.ml-2 {
  margin-left: 0.5rem;
}

.mr-2 {
  margin-right: 0.5rem;
}
</style>
